import React, { useState } from "react";
import { Card, Row, Col, OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  Lightning,
  Activity,
  CreditCard,
  DashCircle,
  XCircle,
  CloudUpload,
  CurrencyDollar,
  Box,
  ChevronUp,
  ChevronDown,
} from "react-bootstrap-icons";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faClock,
  faTimesCircle,
  faExclamationCircle,
  faClipboardCheck,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";

const ClaimsSummaryInfoCard = () => {
  const [open, setOpen] = useState(true);
  const { claimStatisticsData } = useSelector((state) => state.claim);

  // Financial items
  const financialItems = [
    {
      label: "Gross",
      value: claimStatisticsData?.data?.claimtot_grossamount,
      icon: <Activity />,
      color: "#059669",
    },
    {
      label: "Discount",
      value: claimStatisticsData?.data?.claimtot_discamnt,
      icon: <XCircle />,
      color: "#ef4444",
    },
    {
      label: "Deductible",
      value: claimStatisticsData?.data?.claimtot_patshreamount,
      icon: <DashCircle />,
      color: "#10b981",
    },
    {
      label: "Net",
      value: claimStatisticsData?.data?.claimtot_netamount,
      icon: <Lightning />,
      color: "#6366f1",
    },

    {
      label: "VAT",
      value: claimStatisticsData?.data?.claimtot_vatamount,
      icon: <CreditCard />,
      color: "#ef4444",
    },

    {
      label: "Submitted",
      value: claimStatisticsData?.data?.claimtot_submtamount,
      icon: <CloudUpload />,
      color: "#6366f1",
    },
    {
      label: "Approved",
      value: claimStatisticsData?.data?.claimtot_apprveamount,
      icon: <CurrencyDollar />,
      color: "#22c55e",
    },
  ];

  // Define statusTypes based on provided data
  const statusTypes = [
    { key: "approved", title: "Approved" },
    { key: "error", title: "Error" },
    { key: "noresp", title: "No Response" },
    { key: "partial", title: "Partially Approved" },
    { key: "pended", title: "Pended" },
    { key: "rejected", title: "Rejected" },
    { key: "cancel", title: "Cancelled" },
    { key: "queue", title: "Queued" },
    { key: "adjudication", title: "Adjudication" },
    { key: "valid", title: "Ready to Submit" },
    { key: "invalid", title: "Invalid" },
    { key: "validation error", title: "Validation Error" },
    { key: "inprocess", title: "In Process" },
  ];

  // Define statusConfig for badge styling with consistent keys and colors
  const statusConfig = {
    approved: { bg: "#d9f8eb", color: "#00894f", icon: faCheckCircle },
    error: { bg: "#fde6d8", color: "#ac5a2b", icon: faExclamationCircle },
    noresp: { bg: "#08101b", color: "#91979e", icon: faExclamationCircle },
    partial: { bg: "#C2EEF6", color: "#0da9bd", icon: faCheckCircle },
    pended: { bg: "#d9e7fa", color: "#215cac", icon: faClock },
    rejected: { bg: "#fbdbe1", color: "#b82c46", icon: faTimesCircle },
    cancel: { bg: "#ffe0fe", color: "#da23aa", icon: faTimesCircle },
    queue: { bg: "#fefad2", color: "#a7970d", icon: faClipboardCheck },
    adjudication: { bg: "#e8dff7", color: "#6f42c1", icon: faClipboardCheck },
    valid: { bg: "#d8f3ff", color: "#197aa4", icon: faCheckCircle },
    invalid: { bg: "#fde6d8", color: "#ac5a2b", icon: faExclamationCircle },
    "validation error": {
      bg: "#fff3cd", // Bootstrap warning background
      color: "#856404", // Bootstrap warning text
      icon: faExclamationTriangle,
    },
    inprocess: { bg: "#ffe6cc", color: "#cc6600", icon: faClock },
  };

  // Calculate total count from claimStatisticsData
  const totalCount = claimStatisticsData?.total_rows || 0;

  // Tooltip renderer
  const renderTooltip = (title, config) => (
    <Tooltip
      id={`tooltip-${title.toLowerCase()}`}
      style={{
        "--bs-tooltip-bg": config.color,
        "--bs-tooltip-color": config.bg,
      }}
      className="rounded"
    >
      {title}
    </Tooltip>
  );

  return (
    <Card className="shadow-sm mt-3 rounded">
      <Card.Header
        onClick={() => setOpen(!open)}
        className="d-flex justify-content-between badge-subtle-info text-info align-items-center cursor-pointer"
      >
        <strong className="fs-11">Summary Information</strong>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </Card.Header>

      {open && (
        <Card.Body className="p-2 border-info border-2 border">
          {/* Financial Info */}
          <Row>
            {financialItems.map((item, index) => (
              <Col
                key={index}
                xs={12}
                sm={6}
                md={4}
                className="d-flex align-items-center"
              >
                <div className="me-2 fs-10 text-muted">{item.icon}</div>
                <div className="flex-grow-1 fs-12">{item.label}</div>
                <div
                  className="fw-semibold fs-11"
                  style={{ color: item.color }}
                >
                  {Number(item.value || 0).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </div>
              </Col>
            ))}
          </Row>

          <hr className="border-1 border-info my-1 py-0" />

          {/* Status Info with Badges */}
          <div className="d-flex flex-wrap mt-3 gap-2 mb-3">
            {/* Total Badge */}
            <OverlayTrigger
              placement="top"
              overlay={renderTooltip("Total Claims", {
                bg: "#e6e8ec",
                color: "#6366f1",
              })}
            >
              <div
                className="badge rounded-pill d-flex align-items-center gap-1 fs-11"
                style={{
                  backgroundColor: "#e6e8ec",
                  color: "#6366f1",
                  padding: "0.25em 0.6em",
                  cursor: "pointer",
                }}
              >
                <Box size={12} />
                {totalCount}
              </div>
            </OverlayTrigger>

            {/* Dynamic Status Badges */}
            {statusTypes.map((statusItem) => {
              const count =
                claimStatisticsData?.data?.[`claimtot_${statusItem.key}`];
              if (!count || count === 0 || count === "0") return null;

              const config =
                statusConfig[statusItem.key] || statusConfig.rejected;

              return (
                <OverlayTrigger
                  key={statusItem.key}
                  placement="top"
                  overlay={renderTooltip(statusItem.title, config)}
                >
                  <div
                    className="badge rounded-pill d-flex align-items-center gap-1 fs-11"
                    style={{
                      backgroundColor: config.bg,
                      color: config.color,
                      padding: "0.25em 0.6em",
                      cursor: "pointer",
                    }}
                  >
                    <FontAwesomeIcon icon={config.icon} size="xs" />
                    {count}
                  </div>
                </OverlayTrigger>
              );
            })}
          </div>
        </Card.Body>
      )}
    </Card>
  );
};

export default ClaimsSummaryInfoCard;
